import type { TaskStep } from "@/services/api/data-hub/types";

/** Named stages shown in the Generate Report dialog. */
export type PdfGenerationStage =
  | "requesting"
  | "configuring"
  | "mapping"
  | "generating"
  | "downloading"
  | "complete"
  | "failed";

export const PDF_GENERATION_STAGE_LABELS: Record<
  Exclude<PdfGenerationStage, "complete" | "failed">,
  string
> = {
  requesting: "Requesting",
  configuring: "Configuring",
  mapping: "Mapping",
  generating: "Generating",
  downloading: "Downloading",
};

const STAGE_ORDER: Exclude<PdfGenerationStage, "complete" | "failed">[] = [
  "requesting",
  "configuring",
  "mapping",
  "generating",
  "downloading",
];

let peakProgressPercentage = 0;

/** Reset monotonic progress tracking at the start of a new PDF run. */
export function resetPdfProgressTracking(): void {
  peakProgressPercentage = 0;
}

function stageFloorPercentage(stage: PdfGenerationStage): number {
  if (stage === "complete") return 100;
  if (stage === "failed") return peakProgressPercentage;
  const idx = stageIndex(stage);
  if (idx < 0) return 0;
  return Math.round((idx / STAGE_ORDER.length) * 100);
}

function resolveMonotonicProgress(
  stage: PdfGenerationStage,
  reported?: number,
): number {
  if (stage === "complete") {
    peakProgressPercentage = 100;
    return 100;
  }
  if (stage === "failed") {
    if (reported != null) {
      peakProgressPercentage = Math.max(
        peakProgressPercentage,
        Math.min(100, Math.max(0, reported)),
      );
    }
    return peakProgressPercentage;
  }

  const reportedClamped = Math.min(100, Math.max(0, reported ?? 0));
  const floor = stageFloorPercentage(stage);
  const next = Math.max(peakProgressPercentage, reportedClamped, floor);
  peakProgressPercentage = next;
  return next;
}

/** Strip technical details from PDF errors before showing them in the UI. */
export function formatPdfGenerationError(
  error: string | undefined,
): string {
  if (!error?.trim()) {
    return "An unexpected error occurred. Please try again.";
  }

  let message = error.trim();

  if (/for url:\s*/i.test(message)) {
    message = message.split(/for url:\s*/i)[0]?.trim() ?? message;
  }

  message = message.replace(/https?:\/\/\S+/gi, "").replace(/\s{2,}/g, " ").trim();

  if (/internal server error|500\s+server error/i.test(message)) {
    return "The PDF service encountered an error. Please try again in a moment.";
  }
  if (/timed?\s*out|timeout/i.test(message)) {
    return "Report generation timed out. Try again, or check that local PDF generation is running.";
  }
  if (/^\d{3}\b/.test(message)) {
    const status = message.match(/^(\d{3})\b/)?.[1];
    if (status) {
      return `Report generation failed (${status}). Please try again.`;
    }
  }

  return message || "An unexpected error occurred. Please try again.";
}

export interface PdfGenerationUiState {
  stage: PdfGenerationStage;
  statusMessage?: string;
  progressPercentage: number;
  steps: TaskStep[];
  error?: string;
  taskId?: string;
}

function stageIndex(
  stage: PdfGenerationStage,
): number {
  if (stage === "complete") return STAGE_ORDER.length;
  if (stage === "failed") return -1;
  return STAGE_ORDER.indexOf(stage);
}

/** Map a stage to TaskStep rows for TaskStepsProgress. */
export function buildPdfGenerationSteps(
  activeStage: PdfGenerationStage,
  options?: { detail?: string; failedStage?: PdfGenerationStage },
): TaskStep[] {
  const activeIdx = stageIndex(activeStage);
  const failedIdx =
    options?.failedStage != null ? stageIndex(options.failedStage) : -1;

  return STAGE_ORDER.map((stage, idx) => {
    const label = PDF_GENERATION_STAGE_LABELS[stage];
    let status: TaskStep["status"] = "pending";
    let detail: string | undefined;

    if (failedIdx >= 0 && idx === failedIdx) {
      status = "failed";
      detail = options?.detail;
    } else if (activeStage === "complete" || idx < activeIdx) {
      status = "done";
    } else if (idx === activeIdx && activeStage !== "failed") {
      status = "running";
      detail = options?.detail;
    }

    return { label, status, kind: "step" as const, detail };
  });
}

/** Client-side progress before a server task exists. */
export function createLocalPdfProgress(
  stage: PdfGenerationStage,
  statusMessage?: string,
): PdfGenerationUiState {
  const idx = stageIndex(stage);
  const estimated =
    stage === "complete"
      ? 100
      : stage === "failed"
        ? peakProgressPercentage
        : Math.round(((idx + 0.5) / STAGE_ORDER.length) * 100);

  return {
    stage,
    statusMessage,
    progressPercentage: resolveMonotonicProgress(stage, estimated),
    steps: buildPdfGenerationSteps(stage, { detail: statusMessage }),
  };
}

/** Merge API task status into UI state (rebuilds step list from stage). */
export function mapTaskStatusToPdfProgress(
  status: {
    stage?: string | null;
    status_message?: string | null;
    progress_percentage?: number;
    steps?: TaskStep[];
    status: string;
    error?: string | null;
    task_id?: string;
  },
): PdfGenerationUiState {
  const stage = normalizeStage(status.stage, status.status);
  const steps = buildPdfGenerationSteps(stage, {
    detail: status.status_message ?? undefined,
    failedStage: stage === "failed" ? inferFailedStage(status.steps) : undefined,
  });

  return {
    stage,
    statusMessage: status.status_message ?? undefined,
    progressPercentage: resolveMonotonicProgress(
      stage,
      status.progress_percentage,
    ),
    steps,
    error: status.error ?? undefined,
    taskId: status.task_id,
  };
}

function normalizeStage(
  stage: string | null | undefined,
  taskStatus: string,
): PdfGenerationStage {
  if (taskStatus === "failed" || taskStatus === "cancelled") return "failed";
  if (taskStatus === "completed") return "complete";

  const normalized = (stage ?? "").toLowerCase();
  if (
    normalized === "requesting" ||
    normalized === "configuring" ||
    normalized === "mapping" ||
    normalized === "generating" ||
    normalized === "downloading"
  ) {
    return normalized;
  }

  if (taskStatus === "pending" || taskStatus === "queued") return "requesting";
  return "generating";
}

function inferFailedStage(steps?: TaskStep[]): PdfGenerationStage | undefined {
  const failed = steps?.find((s) => s.status === "failed");
  if (!failed) return "generating";
  const match = STAGE_ORDER.find(
    (s) => PDF_GENERATION_STAGE_LABELS[s] === failed.label,
  );
  return match;
}
